import { LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import {
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useChangePasswordMutation } from '../api/authApi'
import key from '../../../assets/Login/key.svg'
import success from '../../../assets/Login/success.svg'
import { Icon } from '../../../utils/Icon'
import { ResetPasswordForm } from '../types'
import { useLogout } from '../../../app/hooks/useLogout'

export const ResetPassword: React.FC<any> = () => {
  const [changePassword, { isLoading, isSuccess }] = useChangePasswordMutation()
  const [showOld, setShowOld] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const navigate = useNavigate()
  const logout = useLogout()

  useEffect(() => {
    if (isSuccess) {
      const timeout = setTimeout(() => {
        logout()
      }, 2000)

      return () => clearTimeout(timeout)
    }
  }, [isSuccess])

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
  } = useForm<ResetPasswordForm>()

  async function onSubmit(values: ResetPasswordForm) {
    changePassword({
      old_password: values.old_password,
      new_password: values.password,
      confirm_password: values.confirm,
    })
  }

  const loadingUI = (
    <Center>
      <Spinner size="xl" />
    </Center>
  )

  const resetPasswordUI = (
    <>
      <Flex direction="column" alignItems="center">
        <Icon w={12} h={12} src={key} mx="auto" />
        <Text as="h2" fontSize={{ base: 'xl', sm: '2xl' }} fontWeight="bold" mt={6}>
          Change your password
        </Text>
        <Text textAlign="center" fontSize="sm" mt={4}>
          Enter your current password and choose a new one.
        </Text>
      </Flex>
      <Center mt={14}>
        <Flex
          direction="column"
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          w="100%"
          maxW="600px"
        >
          <FormControl isInvalid={errors.old_password !== undefined}>
            <InputGroup size="lg">
              <InputLeftElement w={{ base: 10, sm: 14 }} pointerEvents="none">
                <LockIcon color="gray.200" />
              </InputLeftElement>
              <Input
                type={showOld ? 'text' : 'password'}
                placeholder="Current password"
                pl={{ base: 10, sm: 14 }}
                {...register('old_password', { required: 'This is required' })}
              />
              <InputRightElement>
                <Button
                  size="lg"
                  onClick={() => setShowOld(!showOld)}
                  variant="unstyled"
                  _focus={{ boxShadow: 'none' }}
                >
                  {showOld ? (
                    <ViewOffIcon color="gray.300" />
                  ) : (
                    <ViewIcon color="gray.300" />
                  )}
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.old_password?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={errors.password !== undefined} mt={4}>
            <InputGroup size="lg">
              <InputLeftElement w={{ base: 10, sm: 14 }} pointerEvents="none">
                <LockIcon color="gray.200" />
              </InputLeftElement>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="New password"
                pl={{ base: 10, sm: 14 }}
                {...register('password', {
                  required: 'This is required',
                  minLength: {
                    value: 8,
                    message: 'Password should be at least 8 characters',
                  },
                })}
              />
              <InputRightElement>
                <Button
                  size="lg"
                  onClick={() => setShowPassword(!showPassword)}
                  variant="unstyled"
                  _focus={{ boxShadow: 'none' }}
                >
                  {showPassword ? (
                    <ViewOffIcon color="gray.300" />
                  ) : (
                    <ViewIcon color="gray.300" />
                  )}
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={errors.confirm !== undefined} mt={4}>
            <InputGroup size="lg">
              <InputLeftElement w={{ base: 10, sm: 14 }} pointerEvents="none">
                <LockIcon color="gray.200" />
              </InputLeftElement>
              <Input
                type={showConfirm ? 'text' : 'password'}
                placeholder="Confirm new password"
                pl={{ base: 10, sm: 14 }}
                {...register('confirm', {
                  required: 'This is required',
                  validate: {
                    matchesPreviousPassword: (value) =>
                      value === getValues('password') || 'Passwords should match!',
                  },
                })}
              />
              <InputRightElement>
                <Button
                  size="lg"
                  onClick={() => setShowConfirm(!showConfirm)}
                  variant="unstyled"
                  _focus={{ boxShadow: 'none' }}
                >
                  {showConfirm ? (
                    <ViewOffIcon color="gray.300" />
                  ) : (
                    <ViewIcon color="gray.300" />
                  )}
                </Button>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage>{errors.confirm?.message}</FormErrorMessage>
          </FormControl>

          <Button mt={4} type="submit" colorScheme="blue" mx="auto" display="block">
            Change Password
          </Button>
        </Flex>
      </Center>
    </>
  )

  const resetPasswordSuccessUI = (
    <Flex direction="column" alignItems="center">
      <Icon w={12} h={12} src={success} mx="auto" />
      <Text fontSize="2xl" mt={6}>
        Password Changed
      </Text>
      <Text textAlign="center" fontSize="sm" mt={4}>
        Your password has been successfully changed.
      </Text>

      <Button
        w="full"
        variant="main"
        h={12}
        isLoading={isLoading}
        fontSize="md"
        mt={14}
        onClick={() => navigate('/', { replace: true })}
      >
        Continue
      </Button>
    </Flex>
  )

  return isLoading ? loadingUI : isSuccess ? resetPasswordSuccessUI : resetPasswordUI
}
